import nameModelValidator from 'forms/shared/validators/models/name/name';
import alias from 'forms/shared/validators/models/alias/alias';
import address from 'forms/shared/validators/models/location/models/address';
import usAddress from 'forms/shared/validators/models/location/models/usAddress';
import birthplaceWithoutCounty from 'forms/shared/validators/models/location/models/birthplaceWithoutCounty';
import physicalAddress from 'forms/shared/validators/models/physicalAddress/physicalAddress';
import { hasYesOrNo, locationIsEmpty } from 'models/validate';
import { isInternational, isPO } from 'helpers/location';
import {
  MOTHER,
  FATHER,
  STEPFATHER,
  STEPMOTHER,
  CHILD,
  NEVER_MARRIED,
  FATHER_IN_LAW,
  MOTHER_IN_LAW,
  immediateFamilyOptions,
  immediateAdditionalFamilyOptions,
  relativeCitizenshipDocumentationOptions,
  relativeResidentDocumentationOptions,
} from 'constants/enums/relationshipOptions';
import {
  OTHER, DEFAULT_EARLIEST, DEFAULT_LATEST, TODAY
} from 'constants/dateLimits';
import { sortSafeDateTimes, getSafeDateTime, setToStartOfMonth } from 'helpers/date';
import {
  ValidateJSModel, ValidateJSOptions, BranchCollectionModel, AliasModel
} from 'forms/shared/types';
import { DateModelValidatorOptions } from 'forms/shared/validators/models/date/types';
import { lettersAndNumbersPattern } from 'constants/patterns';
import { DATE_IN_THE_PAST, LAST_CONTACT_DATE_TOO_EARLY, REQUIRED } from 'constants/errors';
import { SanitizeDateRange } from 'forms/shared/validators/models/dateRange/instance';
import { isPVQForm } from 'helpers/form';
import { foreignContactMethodOptions } from 'constants/enums/foreignActivityOptions';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { DeepSafePartial } from 'types/types';
import { countryString } from '../../../../../validators/location';
import { RelationshipsRelativeItemModel } from '../types';

const alphanumericRule = {
  presence: true,
  hasValue: {
    validator: {
      format: {
        pattern: lettersAndNumbersPattern,
        message: 'error.alphanumeric.pattern',
      },
      length: {
        maximum: 30,
        message: 'error.maximumLength',
      },
    },
  },
};

/** Helper functions */
export const isCitizen = (attributes: DeepSafePartial<RelationshipsRelativeItemModel>) => !!(
  attributes?.Citizenship?.value?.includes
  && attributes?.Citizenship.value.includes('United States')
);

export const isLiving = (attributes: DeepSafePartial<RelationshipsRelativeItemModel>) => attributes?.IsDeceased && attributes?.IsDeceased.value === 'No';

export const livesInUS = (attributes: DeepSafePartial<RelationshipsRelativeItemModel>) => !!(
  attributes?.Address?.country
  && countryString(attributes?.Address.country) === 'United States'
);

export const isLivingNonCitizen = (attributes: DeepSafePartial<RelationshipsRelativeItemModel>) => isLiving(attributes) && !isCitizen(attributes);

export const wasBornInUS = (attributes: DeepSafePartial<RelationshipsRelativeItemModel>) => !!(
  attributes?.Birthplace?.country
  && countryString(attributes?.Birthplace.country) === 'United States'
);

export const hasApoAddress = (attributes: DeepSafePartial<RelationshipsRelativeItemModel>) => !!(attributes?.AlternateAddress?.HasDifferentAddress?.value === 'Yes');

// requireCitizenshipDocumentation returns true if it should be required
// The final rule indicates in any scenario where the APO/FPO screen contains a response (when not livining in us), is a U.S. citizen and place of birth isn't U.S.
export const requireCitizenshipDocumentation = (
  attributes: DeepSafePartial<RelationshipsRelativeItemModel>,
  options: ValidateJSOptions
) => {
  if (options?.requireRelationshipRelativesForeignBornDoc) {
    return !!(
      (isCitizen(attributes) && !wasBornInUS(attributes) && !isLiving(attributes))
      || (isCitizen(attributes)
        && !wasBornInUS(attributes)
        && (livesInUS(attributes) || hasApoAddress(attributes))
      )
      || (isCitizen(attributes) && !wasBornInUS(attributes) && !livesInUS(attributes)) // maintaining these as a separate clause from above for rule validation analysis
    );// effectivly these three rules equate to ((isCitizen(attributes) && !wasBornInUS(attributes)))
  }
  return false;
};

export const requireResidenceDocumentation = (
  attributes: DeepSafePartial<RelationshipsRelativeItemModel>,
  options: ValidateJSOptions
) => options?.requireRelationshipRelativesUSResidenceDoc
&& isLivingNonCitizen(attributes)
  && livesInUS(attributes);

export const requireRelativeContactDescription = (
  attributes: DeepSafePartial<RelationshipsRelativeItemModel>,
  options: ValidateJSOptions
) => options?.requireRelationshipRelativesUSResidenceDoc && isLivingNonCitizen(attributes);

export const hasAliasDuplicates = (value: DeepSafePartial<BranchCollectionModel<AliasModel>>) => {
  let result = false;
  if (value?.items) {
    const cleanItems = value.items.map((x) => x?.Item);
    // remove the type pollution for validation from the date range
    // there may be more type pollution possible that wasn't available during the testing
    cleanItems.forEach((v) => {
      const today = new Date();

      if (v) {
        const dates = SanitizeDateRange(v?.Dates);
        if (dates?.present && dates?.to) {
          dates.to.day = today.getDate().toString();
          dates.to.year = today.getFullYear().toString();
          dates.to.month = (today.getMonth() + 1).toString();
        }
        v.Dates = { ...dates }; // eslint-disable-line no-param-reassign
      }
    });

    // compare items on the list case insensitive and ignore Reason, Has, and index differences.
    const items = cleanItems.map((x) => JSON.stringify({
      ...x, Reason: '', index: '', Has: ''
    }).toLowerCase());
    const uniqueItems = new Set(items);
    result = !(items.length === uniqueItems.size);
  }
  return result;
};

export const relationshipsRelativeItemValidator: ValidateJSModel<RelationshipsRelativeItemModel> = {
  AlternateAddressNotApplicable: {},

  EmployerAddressNotApplicable: {},

  EmployerNotApplicable: {},

  EmployerRelationshipNotApplicable: {},

  MaidenSameAsListed: {},

  ParentNotApplicable: {},

  DocumentNumberNotApplicable: {},

  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.ParentNotApplicable?.applicable !== false) {
      return {
        presence: true,
        model: {
          validator: nameModelValidator,
        },
      };
    }
    return {};
  },

  Relation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (options?.maritalStatus === NEVER_MARRIED) {
      return {
        hasValue: {
          validator: {
            length: { minimum: 1 },
            exclusion: {
              within: [FATHER_IN_LAW, MOTHER_IN_LAW],
              message: 'error.relative.inlaw',
            }
          }
        },
      };
    }
    return {
      hasValue: { validator: { length: { minimum: 1 } } },
    };
  },

  Birthdate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.ParentNotApplicable?.applicable !== false) {
      if (attributes?.Relation) {
        const parentLatestDate = getSafeDateTime(options?.applicantBirthdate).datetime?.minus({ days: 1 }) || TODAY;
        switch (attributes?.Relation.value) {
          case CHILD: {
            const earliestDate = getSafeDateTime(options?.applicantBirthdate).datetime?.plus({ days: 1 }) || TODAY.minus({ years: 100 });
            return {
              presence: true,
              date: {
                earliest: earliestDate,
                tooEarly: 'error.child.dob.min',
                latest: DEFAULT_LATEST,
              },
            };
          }
          case MOTHER: {
            return {
              presence: true,
              date: {
                requireYear: false,
                earliest: DEFAULT_EARLIEST,
                latest: parentLatestDate,
                tooLate: 'error.relative.parent.max',
                tooEarly: 'error.birthdateTooFar.min',
              },
            };
          }
          case FATHER: {
            return {
              presence: true,
              date: {
                requireYear: false,
                earliest: DEFAULT_EARLIEST,
                latest: parentLatestDate,
                tooLate: 'error.relative.parent.max',
                tooEarly: 'error.birthdateTooFar.min',
              },
            };
          }
          case STEPMOTHER:
          case STEPFATHER:
          default: {
            return {
              presence: true,
              date: {
                ...OTHER,
                tooEarly: 'error.birthdateTooFar.min',
              },
            };
          }
        }
      }

      return {
        presence: true,
        date: { ...OTHER },
      };
    }
    return {};
  },

  Birthplace: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.ParentNotApplicable?.applicable !== false) {
      return {
        presence: true,
        location: { validator: birthplaceWithoutCounty },
      };
    }
    return {};
  },

  Citizenship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.ParentNotApplicable?.applicable !== false) {
      return { hasValue: true, country: true };
    }
    return {};
  },

  IsDeceased: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.ParentNotApplicable?.applicable !== false) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },

  MaidenName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.Relation
      && attributes?.Relation.value === MOTHER
      && attributes?.ParentNotApplicable?.applicable !== false) {
      if (attributes?.MaidenSameAsListed?.value !== 'Yes') {
        return {
          presence: true,
          model: { validator: nameModelValidator },
        };
      }
    }

    return {};
  },

  Aliases: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.ParentNotApplicable?.applicable === false) {
      return {};
    }

    if (
      attributes?.Relation?.value
      && options?.requireRelationshipRelativesAdditionalFamily
      && !immediateAdditionalFamilyOptions.includes(attributes?.Relation.value)
    ) {
      return {};
    }

    if (
      attributes?.Relation?.value
      && !options?.requireRelationshipRelativesAdditionalFamily
      && !immediateFamilyOptions.includes(attributes?.Relation.value)
    ) {
      return {};
    }

    if (hasAliasDuplicates(value)) {
      return {
        presence: true,
        length: { is: -2, message: 'error.relative.duplicateAlias' },
        branchCollection: {
          validator: alias,
          hideMaiden: attributes?.Relation?.value === MOTHER && attributes?.MaidenSameAsListed?.value === 'Yes',
          earliest: setToStartOfMonth(attributes?.Birthdate),
          tooEarly: 'error.date.relativeBirthdate',
        },
      };
    }
    return {
      presence: true,
      branchCollection: {
        validator: alias,
        hideMaiden: attributes?.Relation?.value === MOTHER && attributes?.MaidenSameAsListed?.value === 'Yes',
        earliest: setToStartOfMonth(attributes?.Birthdate),
        tooEarly: 'error.date.relativeBirthdate',
      },
    };
  },

  Address: (value, attributes, attributeName, options) => {
    if (attributes?.IsDeceased?.value === 'Yes'
      || attributes?.ParentNotApplicable?.applicable === false
      || attributes?.AddressNotApplicable?.applicable === false
    ) { return {}; }

    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        location: { validator: pvqLocationModelValidator },
      };
    }

    return {
      presence: true,
      location: { validator: address, militaryAddress: false },
    };
  },

  AddressNotApplicable: {},
  AddressExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.IsDeceased?.value === 'Yes'
      || attributes?.ParentNotApplicable?.applicable === false
    ) { return {}; }
    if (attributes?.AddressNotApplicable?.applicable === false) {
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },

  AlternateAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.IsDeceased?.value === 'Yes'
      || attributes?.ParentNotApplicable?.applicable === false
      || attributes?.AlternateAddressNotApplicable?.applicable === false
      || attributes?.AddressNotApplicable?.applicable === false
    ) { return {}; }

    if (attributes?.Address && isInternational(attributes?.Address)) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: true,
          allowPOBox: false,
          hasTelephone: false,
        },
      };
    }

    if (attributes?.Address && isPO(attributes?.Address)) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: true,
          onlyMilitaryAddress: true,
          allowPOBox: false,
          hasTelephone: false,
        },
      };
    }

    return {};
  },

  AlternateAddressExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (
      attributes?.AlternateAddress?.HasDifferentAddress?.value === 'IDontKnow'
      && attributes?.IsDeceased?.value === 'No'
      && attributes?.Address?.country
      && countryString(attributes?.Address.country) !== 'United States'
      && attributes?.AddressNotApplicable?.applicable !== false
    ) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  CitizenshipDocumentation: (value, attributes, attributeNane, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (requireCitizenshipDocumentation(attributes, options)) {
      return {
        presence: true,
        hasValue: {
          validator: {
            inclusion: relativeCitizenshipDocumentationOptions,
          },
        },
      };
    }

    return {};
  },

  OtherCitizenshipDocumentation: (value, attributes, attributeNane, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (requireCitizenshipDocumentation(attributes, options)
      && attributes?.CitizenshipDocumentation?.value === 'Other') {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  // DocumentNumber is used for citizens second pass isn't available, unnessasary but permitted for "other"
  // driven by the value of CitizenshipDocumentation
  DocumentNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (requireCitizenshipDocumentation(attributes, options)
      && (attributes?.CitizenshipDocumentation?.value !== 'Other' || attributes?.DocumentNumber?.value)) {
      return {
        presence: true,
        hasValue: {
          validator: {
            format: {
              pattern: lettersAndNumbersPattern,
              message: 'error.alphanumeric.pattern',
            },
          },
        },
      };
    }

    return {};
  },

  CourtName: {},

  CourtAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.CourtAddressNotApplicable?.applicable === false) {
      return {};
    }

    if (requireCitizenshipDocumentation(attributes, options)) {
      if (!locationIsEmpty(value, true)) {
        return {
          presence: true,
          location: { validator: usAddress },
        };
      }
    }

    return {};
  },

  CourtAddressNotApplicable: {},
  CourtAddressExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (
      attributes?.CourtAddressNotApplicable?.applicable === false
      && requireCitizenshipDocumentation(attributes, options)
    ) {
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },

  Document: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (requireResidenceDocumentation(attributes, options)) {
      return {
        presence: true,
        hasValue: {
          validator: {
            inclusion: relativeResidentDocumentationOptions,
          },
        },
      };
    }

    return {};
  },

  DocumentComments: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (
      requireResidenceDocumentation(attributes, options)
      && attributes?.Document?.value === 'Other') {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  OptionalComments: {},

  // ResidenceDocumentNumber is used for non-citizens second path is available for all, unnessasary for "other"
  // driven by the value of Document
  ResidenceDocumentNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (requireResidenceDocumentation(attributes, options)
      && attributes?.DocumentNumberNotApplicable?.applicable !== false
      && (attributes?.Document?.value !== 'Other' || attributes?.ResidenceDocumentNumber?.value)) {
      return alphanumericRule;
    }

    return {};
  },

  // DocumentNumberExplanation If second Pass (IDK) is implemnented than this need to be filled in
  DocumentNumberExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.DocumentNumberNotApplicable?.applicable === false // if someone ticked I don't know
      && (requireResidenceDocumentation(attributes, options) || requireCitizenshipDocumentation(attributes, options)) // they need to have document number checked for some reason
      && !((requireResidenceDocumentation(attributes, options) && attributes?.Document?.value === 'Other')
        || (requireCitizenshipDocumentation(attributes, options) && attributes?.CitizenshipDocumentation?.value === 'Other')) // and they did not select "Other" for the required field
    ) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  Expiration: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const { Document, Birthdate } = attributes;
    // Expiration only required for the following:
    // NonCitizenI766 = Employment, NonCitizenI94 = Arrival, NonCitizenI20 = F1, NonCitizenDS2019 = J1
    const expirationRequired = ['Employment', 'Arrival', 'F1', 'J1'].includes(Document?.value || '');
    if (requireResidenceDocumentation(attributes, options) && expirationRequired) {
      return {
        presence: true,
        date: {
          earliest: Birthdate,
          tooEarly: 'relationships.relatives.error.documentExpiration.before'
        },
      };
    }

    return {};
  },

  FirstContact: (value, attributes, attributeName, options) => {
    if (attributes?.FirstContactNotApplicable?.applicable === false) {
      return {};
    }
    if (requireRelativeContactDescription(attributes, options)) {
      if (isPVQForm(options?.formType) && attributes?.NoContactWithFamily?.checked === true) {
        return {};
      }
      const { Birthdate } = attributes;
      const { applicantBirthdate } = options;

      const sortedDates = sortSafeDateTimes([
        { ...getSafeDateTime(Birthdate), field: 'Birthdate' },
        { ...getSafeDateTime(applicantBirthdate), field: 'applicantBirthdate' },
      ]);

      const earliestDate = sortedDates.length ? setToStartOfMonth(sortedDates[sortedDates.length - 1]) : null;

      return {
        presence: true,
        date: {
          earliest: earliestDate?.datetime,
          latest: DEFAULT_LATEST,
          tooEarly: earliestDate?.field === 'Birthdate' ? 'error.date.relativeBirthdate' : 'error.date.min'
        },
      };
    }

    return {};
  },

  FirstContactNotApplicable: {},
  FirstContactExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (requireRelativeContactDescription(attributes, options)
      && attributes?.FirstContactNotApplicable?.applicable === false) {
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },

  LastContact: (value, attributes, attributeName, options) => {
    if (requireRelativeContactDescription(attributes, options)) {
      if (isPVQForm(options?.formType) && attributes?.NoContactWithFamily?.checked === true) {
        return {};
      }
      const dateLimits: DateModelValidatorOptions = {
        latest: DEFAULT_LATEST,
        earliest: DEFAULT_EARLIEST,
        tooEarly: DATE_IN_THE_PAST,
      };
      if (attributes?.FirstContact) {
        dateLimits.earliest = attributes?.FirstContact;
        dateLimits.tooEarly = LAST_CONTACT_DATE_TOO_EARLY;
      }

      return {
        presence: true,
        date: dateLimits,
      };
    }

    return {};
  },

  Methods: (value, attributes, attributeName, options) => {
    if (requireRelativeContactDescription(attributes, options)) {
      if (isPVQForm(options?.formType)) {
        return {
          presence: true,
          array: {
            length: { minimum: 1, message: REQUIRED },
            validator: { inclusion: foreignContactMethodOptions },
          }
        };
      }
      return {
        presence: true,
        array: {
          validator: { presence: true },
          length: { minimum: 1, message: REQUIRED },
        },
      };
    }

    return {};
  },

  MethodsComments: (value, attributes, attributeName, options) => {
    // PVQ and SF have the same requirements
    if (attributes?.Methods?.values?.some((i) => i === 'Other')) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  Frequency: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (requireRelativeContactDescription(attributes, options)) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  FrequencyComments: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const shouldCheckFrequencyComments = options?.requireRelationshipRelativesUSResidenceDoc
      && attributes?.Citizenship?.value
      && !isCitizen(attributes)
      && attributes?.IsDeceased?.value === 'No'
      && attributes?.Frequency?.value === 'Other';
    if (shouldCheckFrequencyComments) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  Employer: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.EmployerNotApplicable?.applicable === false) { return {}; }

    if (requireRelativeContactDescription(attributes, options)) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  EmployerAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.EmployerAddressNotApplicable?.applicable === false) { return {}; }

    if (requireRelativeContactDescription(attributes, options)) {
      return {
        presence: true,
        location: { validator: address },
      };
    }

    return {};
  },

  HasAffiliation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.EmployerRelationshipNotApplicable?.applicable === false) { return {}; }

    if (requireRelativeContactDescription(attributes, options)) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }

    return {};
  },

  EmployerRelationship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.HasAffiliation?.value === 'Yes' && attributes?.EmployerRelationshipNotApplicable?.applicable !== false) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  EntryExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.ParentNotApplicable?.applicable === false) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },
  EntryComments: (value, attributes) => ({}),

  MothersMaidenNameComments: {},
  AliasesComments: {},
  AliasesSummaryComments: {},
  DeceasedComments: {},
  ForeignRelativeComments: {},
  EmployerComments: {},
  HasAffiliationComments: {},
  ForeignAffiliationComments: {},
  AddressComments: {},
  AnswerComments: {},
  PhysicalLocationComments: {},
};

export default relationshipsRelativeItemValidator;
